import { useToken } from "@gluestack-style/react";
import FormField from "components/form/FormField";
import CreditCardIconHandler from "components/general/CreditCardIconHandler";
import DismissableContent from "components/general/DismissableContent";
import PageLoader from "components/general/PageLoader";
import PopupModal from "components/general/PopupModal";
import { Box, Button, H4, HStack, Pressable, Select, Text, Typography } from "components/ui";
import { Formik } from "formik";
import { Check, CircleAlert, Plus } from "lucide-react-native";
import React, { useState, useMemo, useCallback, useRef } from "react";
import { ActivityIndicator } from "react-native";
import { useDeletePaymentMutation, useGetPaymentQuery, usePostPaymentMutation } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import * as Yup from "yup";

interface CreditCardFormProps {
	selectedCard: any;
	onSelectCard: (card: any) => void;
}

// Luhn algorithm to validate credit card numbers
const isValidLuhn = (cardNumber: string): boolean => {
	const digits = cardNumber.split("").map(Number);
	let sum = 0;
	let isEven = false;

	// Loop through digits from right to left
	for (let i = digits.length - 1; i >= 0; i--) {
		let digit = digits[i];

		if (isEven) {
			digit *= 2;
			if (digit > 9) {
				digit -= 9;
			}
		}

		sum += digit;
		isEven = !isEven;
	}

	return sum % 10 === 0;
};

// Utility function to detect credit card type
const detectCardType = (cardNumber: string): string | null => {
	const cleanNumber = cardNumber.replace(/\s/g, "");

	// Visa: starts with 4, 13, 16, or 19 digits
	if (/^4/.test(cleanNumber)) {
		return "visa";
	}

	// Mastercard: starts with 51-55, 2221-2720, or 5, 16 digits
	if (/^5[1-5]/.test(cleanNumber) || /^222[1-9]/.test(cleanNumber) || /^22[3-9]/.test(cleanNumber) || /^2[3-6]/.test(cleanNumber) || /^27[0-1]/.test(cleanNumber) || /^2720/.test(cleanNumber)) {
		return "mastercard";
	}

	// American Express: starts with 34 or 37, 15 digits
	if (/^3[47]/.test(cleanNumber)) {
		return "amex";
	}

	return null;
};

// Utility function to get expected length for card type
const getExpectedLength = (cardType: string): number => {
	switch (cardType) {
		case "amex":
			return 15;
		case "visa":
		case "mastercard":
		default:
			return 16;
	}
};

// Memoized regex pattern to avoid recreation
const CARD_PATTERN = /(?<n>[A-Z]+) \*\d+ \((?<expiry>\d{1,2}\/\d{4})\)/;

const CreditCardForm = ({ selectedCard, onSelectCard }: CreditCardFormProps) => {
	const { data: creditTokenCollection, isLoading } = useGetPaymentQuery({});
	const [postCreditCard, { isLoading: isSaving, isError, error }] = usePostPaymentMutation();
	const [deleteCreditCard, { isLoading: isDeleting }] = useDeletePaymentMutation();
	const [errorMessages, setErrorMessages] = React.useState<string>("");
	const [showError, setShowError] = React.useState<boolean>(false);
	const [addNew, setAddNew] = useState(false);
	const [previousCardIds, setPreviousCardIds] = useState<number[]>([]);

	const resolvedErrorBright = useToken("colors", "errorbright");
	const resolvedPrimaryMidGreen = useToken("colors", "secondary");
	const isMobile = useIsMobile();

	// Memoize expensive computations
	const { extractedData, savedCardOptionList, monthOptions, yearOptions } = useMemo(() => {
		if (!creditTokenCollection) {
			return {
				extractedData: [],
				savedCardOptionList: [],
				monthOptions: [],
				yearOptions: [],
			};
		}

		const currentYear = new Date().getFullYear();

		const extractedData = creditTokenCollection
			.map((item: any) => {
				const match = item.paymentMask.match(CARD_PATTERN);
				if (match) {
					return {
						paymentId: item.paymentId,
						cardType: match.groups?.n,
						expiry: match.groups?.expiry,
					};
				}
				return null;
			})
			.filter(Boolean);

		const savedCardOptionList = creditTokenCollection.map((paymentToken) => ({
			value: paymentToken.paymentId,
			label: paymentToken.paymentMask,
		}));

		const monthOptions = Array.from({ length: 12 }, (_, i) => ({
			value: String(i + 1).padStart(2, "0"),
			label: new Date(0, i).toLocaleString("default", { month: "long" }),
		}));

		const yearOptions = Array.from({ length: 20 }, (_, i) => ({
			value: String(currentYear + i),
			label: String(currentYear + i),
		}));

		return { extractedData, savedCardOptionList, monthOptions, yearOptions };
	}, [creditTokenCollection]);

	// Memoize validation schema
	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				name: Yup.string().required("Cardholder name is required").min(3, "Name must be at least 3 characters"),
				number: Yup.string()
					.required("Card number is required")
					.test("card-number-format", "Invalid card number format", function (value) {
						if (!value) return false;
						const cleanNumber = value.replace(/\s/g, "");
						const cardType = detectCardType(cleanNumber);

						if (!cardType) {
							return this.createError({ message: "Unsupported card type" });
						}

						const expectedLength = getExpectedLength(cardType);
						if (cleanNumber.length !== expectedLength) {
							return this.createError({ message: `${cardType.toUpperCase()} cards must be ${expectedLength} digits` });
						}

						if (!isValidLuhn(cleanNumber)) {
							return this.createError({ message: "Invalid card number" });
						}

						return true;
					}),
				month: Yup.string().nullable().required("Expiration month is required"),
				year: Yup.string().nullable().required("Expiration year is required"),
				cvv: Yup.string()
					.required("CVV is required")
					.test("cvv-length", "Invalid CVV", function (value) {
						if (!value) return false;
						const cardNumber = this.parent.number || "";
						const cardType = detectCardType(cardNumber.replace(/\s/g, ""));
						const expectedLength = cardType === "amex" ? 4 : 3;
						return value.length === expectedLength;
					}),
			}),
		[],
	);

	// Auto-select first card when available
	const hasAutoSelectedRef = useRef(false);

	React.useEffect(() => {
		if (!selectedCard && savedCardOptionList.length > 0 && !hasAutoSelectedRef.current) {
			hasAutoSelectedRef.current = true;
			onSelectCard(savedCardOptionList[0]);
		}

		// Reset flag when no cards available
		if (savedCardOptionList.length === 0) {
			hasAutoSelectedRef.current = false;
		}
	}, [selectedCard, savedCardOptionList.length]);

	// Reset auto-selection flag when selectedCard changes externally
	React.useEffect(() => {
		if (selectedCard) {
			hasAutoSelectedRef.current = true;
		}
	}, [selectedCard]);

	// Auto-select newly added card
	React.useEffect(() => {
		if (!creditTokenCollection || previousCardIds.length === 0) {
			return;
		}

		const currentCardIds = creditTokenCollection.map((card: any) => card.paymentId);

		// Check if a new card was added
		if (currentCardIds.length > previousCardIds.length) {
			// Find the newly added card(s)
			const newCardIds = currentCardIds.filter((id) => !previousCardIds.includes(id));

			if (newCardIds.length > 0) {
				// Find the newly added card in the saved card options
				const newCard = savedCardOptionList.find((card) => card.value === newCardIds[0]);

				if (newCard) {
					// Auto-select the newly added card
					onSelectCard(newCard);
					// Reset the previous card IDs to prevent re-triggering
					setPreviousCardIds([]);
				}
			}
		}
	}, [creditTokenCollection, previousCardIds, savedCardOptionList, onSelectCard]);

	// Memoized callbacks
	const handleToggleAddNew = useCallback(() => {
		setAddNew(!addNew);
		if (!addNew) {
			// Store current card IDs when opening the modal
			const currentCardIds = creditTokenCollection?.map((card: any) => card.paymentId) || [];
			setPreviousCardIds(currentCardIds);
			setErrorMessages("");
			setShowError(false);
		}
	}, [addNew, creditTokenCollection]);

	const handleBackdropPress = useCallback(() => {
		setAddNew(false);
		setErrorMessages("");
		setShowError(false);
		setPreviousCardIds([]);
	}, []);

	const handleCardSelect = useCallback(
		(id: string) => {
			// Prevent unnecessary updates if same card is selected
			if (selectedCard?.value === parseInt(id)) {
				return;
			}

			const selected = savedCardOptionList.find((card) => card.value === parseInt(id));
			if (selected) {
				onSelectCard?.(selected);
			}
		},
		[savedCardOptionList, onSelectCard, selectedCard?.value],
	);

	const selectedCardData = useMemo(() => {
		return extractedData.find((card: any) => card?.paymentId === selectedCard?.value);
	}, [extractedData, selectedCard]);

	if (isLoading) {
		return (
			<Box minHeight={200} justifyContent="center" alignItems="center">
				<ActivityIndicator size="large" />
			</Box>
		);
	}

	return (
		<Box gap={24} padding={isMobile ? 0 : 16} paddingHorizontal={12} marginTop={isMobile ? 0 : 24}>
			<Typography>Select a saved card or a new one:</Typography>
			<Box>
				<HStack justifyContent="space-between" alignItems="center">
					<H4>Saved Cards</H4>
					<Pressable
						style={{
							borderRadius: 6,
							paddingHorizontal: 12,
							paddingVertical: 8,
							backgroundColor: resolvedPrimaryMidGreen,
						}}
						onPress={handleToggleAddNew}
					>
						<HStack gap={6} alignItems="center" bg="transparent">
							<Plus color="black" />
							<Typography sx={{ color: addNew ? "white" : "black" }}>Add new</Typography>
						</HStack>
					</Pressable>
				</HStack>
			</Box>

			{savedCardOptionList.length > 0 && (
				<Box position="relative" zIndex={9}>
					<Select isCreditCard defaultValue={selectedCard} value={selectedCard} options={savedCardOptionList} onChange={handleCardSelect} />
				</Box>
			)}

			{selectedCard && selectedCardData && (
				<Box
					sx={{
						shadowColor: "#000",
						shadowOffset: { width: 0, height: 1 },
						shadowOpacity: 0.18,
						shadowRadius: 1.0,
						elevation: 1,
					}}
					borderRadius={12}
					gap={12}
					bg="$neutral100"
					padding={24}
				>
					<Typography sx={{ fontFamily: "Roboto_700Bold" }}>Selected Credit card:</Typography>
					<Box flexDirection="row" alignItems="center" justifyContent="space-between" bg="$basicwhite" borderRadius={12} padding={24}>
						<Box flexDirection={isMobile ? "column" : "row"} gap={12} alignItems={isMobile ? "flex-start" : "center"}>
							<Box padding={6} bg="$basicwhite">
								<CreditCardIconHandler type={selectedCardData.cardType} />
							</Box>
							<Box gap={6}>
								<Text fontFamily="Roboto_700Bold">{selectedCard.label}</Text>
								<Text color="$neutral500">Expires on {selectedCardData.expiry || "Expiry not available"}</Text>
							</Box>
						</Box>
						{!isMobile && (
							<Box>
								<Check />
							</Box>
						)}
					</Box>
				</Box>
			)}

			<PopupModal
				viewHeight={600}
				animationIn={"fadeIn"}
				animationOut={"fadeOut"}
				scrollOffset={1}
				onBackdropPress={handleBackdropPress}
				isVisible={addNew}
				title="Add New Credit Card"
				style={{
					maxWidth: 1240,
					marginLeft: "auto",
					marginRight: "auto",
					width: "100%",
				}}
			>
				<Formik
					initialValues={{
						name: "",
						number: "",
						month: null,
						year: null,
						cvv: "",
					}}
					validationSchema={validationSchema}
					onSubmit={async (values, { resetForm }) => {
						setErrorMessages("");
						setShowError(false);

						try {
							await postCreditCard({
								creditCard: values,
							}).unwrap();

							setAddNew(false);
							resetForm();
							setErrorMessages("");
							setShowError(false);
							// Note: Don't reset previousCardIds here as the useEffect needs it to detect the new card
						} catch (error) {
							const message = (error as any)?.data?.message || "An error occurred while saving the credit card details.";
							setErrorMessages(message);
							setShowError(true);
						}
					}}
				>
					{(formik) => (
						<Box gap={20}>
							<FormField label="Cardholder Name" name="name" placeholder="Name on card" formik={formik} />

							<FormField label="Card Number" name="number" placeholder="Enter card number" formik={formik} />

							<Typography color="$neutral500" sx={{ fontSize: 12 }}>
								Supported: Visa (16 digits), Mastercard (16 digits), American Express (15 digits)
							</Typography>

							<Box gap={6} position="relative" zIndex={9}>
								<HStack flexDirection={isMobile ? "column" : "row"} gap={12}>
									<Box gap={6} flex={1} zIndex={9}>
										<FormField label="Expiration Month" name="month" type="select" options={monthOptions} placeholder="--Please Select--" formik={formik} />
									</Box>

									<Box gap={6} flex={1} zIndex={8}>
										<FormField label="Expiration Year" name="year" type="select" options={yearOptions} placeholder="--Please Select--" formik={formik} />
									</Box>

									<Box gap={6} flex={1} zIndex={7}>
										<FormField label="CVV" name="cvv" placeholder="CVV" formik={formik} />
										<Typography color="$neutral500" sx={{ fontSize: 12 }}>
											3 digits (Visa/Mastercard) or 4 digits (Amex)
										</Typography>
									</Box>
								</HStack>
							</Box>

							{errorMessages && showError && <DismissableContent icon={<CircleAlert color={resolvedErrorBright} />} text={errorMessages} variant="error" onDismiss={() => setShowError(false)} />}

							<Box marginTop={isMobile ? 20 : 0}>
								<Button gap={6} variant="secondary" disabled={isSaving} mt={20} borderRadius={6} onPress={() => formik.handleSubmit()}>
									{isSaving ? <ActivityIndicator size="small" color="black" /> : <Plus color={"black"} />}
									<Typography weight="bold">{isSaving ? "Processing..." : "Add Credit Card"}</Typography>
								</Button>
							</Box>
						</Box>
					)}
				</Formik>
			</PopupModal>
		</Box>
	);
};

export default React.memo(CreditCardForm);
